import { useQuery } from '@tanstack/react-query';
import type { HelpTopicDto, HelpTopicSummaryDto } from '@cpm/shared';
import { api } from './client.js';

/**
 * The help center exists in both shells: the panel reads /api/help, the
 * portal reads its own tenant-safe mirror under /api/portal/help (client
 * audience only). Pages pass the shell they render in.
 */
export type HelpShell = 'panel' | 'portal';

const API_BASE: Record<HelpShell, string> = {
  panel: '/api/help',
  portal: '/api/portal/help',
};

/** Where `help:` links and topic routes live for each shell. */
export const HELP_ROUTE_BASE: Record<HelpShell, string> = {
  panel: '/help',
  portal: '/portal/help',
};

export function useHelpTopics(shell: HelpShell, query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['help', shell, 'topics', q],
    queryFn: () =>
      api.get<HelpTopicSummaryDto[]>(
        q ? `${API_BASE[shell]}/topics?q=${encodeURIComponent(q)}` : `${API_BASE[shell]}/topics`,
      ),
    staleTime: 5 * 60_000,
  });
}

export function useHelpTopic(shell: HelpShell, moduleKey: string, slug: string) {
  return useQuery({
    queryKey: ['help', shell, 'topic', moduleKey, slug],
    queryFn: () => api.get<HelpTopicDto>(`${API_BASE[shell]}/topics/${moduleKey}/${slug}`),
    staleTime: 5 * 60_000,
  });
}
